import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { ALMObject, fetchLearningObjects } from "../../utils/myLearning";
import styles from "./Survey.module.css";
import { getAccessToken, getALMConfig, getALMUser } from "../../utils/global";
import useParam from "../../hooks/useParams";
import {
    PrimeLearningObject,
    PrimeLoInstanceSummary
} from "../../models/PrimeModels";
import type { CourseTrainingProps } from '../../utils/courseButtonProps';

type LocalizedMetadata = {
    locale: string;
    name: string;
};

type APIQuestion = {
    mandatory: boolean;
    questionId: string;
    questionType: "scaleTen" | "likeability" | "text" | "l1Qualitative";
    localizedMetadata: LocalizedMetadata[];
    name: string;
};

type APISurveyData = {
    data: {
        id: string;
        type: string;
        attributes: {
            showAutomatically?: boolean;
            questions: APIQuestion[];
        };
    };
};

const Survey: React.FC<{
                training: PrimeLearningObject;
                courseBtnText?: CourseTrainingProps;
                trainingInstanceId: string;
                title: string;
                instanceSummary: PrimeLoInstanceSummary;
            }> = (props: any) => {
    const {training, courseBtnText, trainingInstanceId, title, instanceSummary} = props;
    const [answers, setAnswers] = useState<{ [key: string]: any }>({});
    const [errors, setErrors] = useState<{ [key: string]: any }>({});
    const [show, setShow] = useState(true);
    const [surveyData, setSurveyData] = useState<APISurveyData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [matchedNotification, setMatchedNotification] = useState<any>(null);

    const baseApiUrl = getALMConfig().primeApiURL;

    useEffect(() => {
        const loId = training.id;
        const loInstanceId = trainingInstanceId;
        const AUTH_HEADER = { 'authorization':  `oauth ${getAccessToken()}` };
        // Helper to recursively fetch notification pages
        const findNotification = async (url: string): Promise<any> => {
            const resp = await fetch(url, { headers: AUTH_HEADER });
            if (!resp.ok) return null;
            const notifData = await resp.json();
            if (Array.isArray(notifData.data)) {
                for (const notif of notifData.data) {
                    if (
                        notif?.attributes?.metadata?.some((meta: any) => meta.id === loInstanceId) &&
                        notif?.attributes?.actionTaken === false
                    ) {
                        return notif;
                    }
                }
            }
            if (notifData.links && notifData.links.next) {
                return await findNotification(notifData.links.next);
            }
            return null;
        };

        const fetchSurvey = async () => {
            setLoading(true);
            setError(null);

            const userResponse = await getALMUser();
            const userId = userResponse?.user?.id;

            try {
                const NOTIF_BASE = `${baseApiUrl}/users/${userId}/userNotifications?page[limit]=10&announcementsOnly=false&language=en_US&userSelectedChannels=course::l1FeedbackPrompt`;
                // 1. Check notification API for loInstanceId
                let foundNotif = await findNotification(NOTIF_BASE);
                if (!foundNotif) {
                    setShow(false);
                    setLoading(false);
                    return;
                } else {
                    setShow(true);
                }
                setMatchedNotification(foundNotif);
                // 2. If found, fetch survey as before
                const url = `${baseApiUrl}/learningObjects/${loId}/instances/${loInstanceId}/l1Feedback`;
                const response = await fetch(url, { headers: AUTH_HEADER });
                if (!response.ok) throw new Error('Failed to fetch survey');
                const data = await response.json();
                setSurveyData(data);

                setTimeout(function () {
                    const secondaryPopup = document.querySelector("#secondary-email-popup-trigger-modal") as HTMLElement || null;
                    const enteringPopup = document.querySelector("#siteEnteringPopupFragmentPath") as HTMLElement || null;

                    if (secondaryPopup) {
                        if (secondaryPopup.classList.contains('show')) {
                            secondaryPopup.classList.add('d-none');
                        }
                    }

                    if (enteringPopup) {
                        if (enteringPopup.classList.contains('show')) {
                            enteringPopup.classList.add('d-none');
                        }
                    }
                }, 1000);
            } catch (err: any) {
                setError(err.message || 'Unknown error');
            } finally {
                setLoading(false);
            }
        };
        fetchSurvey();
    }, [instanceSummary]);

    const handleChange = (qid: string, value: any) => {
        setAnswers((prev) => ({ ...prev, [qid]: value }));
        setErrors((prev) => ({ ...prev, [qid]: ''}));
    };

    const handleClose = () => setShow(false);

    const getLocalizedQuestion = (q: APIQuestion) => {
        // Try to get user's browser language, fallback to en-US, then first available
        const userLocale = navigator.language || 'en-US';
        return (
            q.localizedMetadata.find(m => m.locale === userLocale) ||
            q.localizedMetadata.find(m => m.locale === 'en-US') ||
            q.localizedMetadata[0] || { name: '' }
        ).name;
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

    const newErrors: Record<string, string> = {};
        surveyData?.data.attributes.questions.forEach((q) => {
            if (q.mandatory && !answers[q.questionId]) {
                newErrors[q.questionId] = "This is required";
            }
        });

    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
        }

        setSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);
        const userResponse = await getALMUser();
        const userId = userResponse?.user?.id;
        try {
            const headSearch = document.querySelector('#headerSearchSuggestApi');
            const getServiceURL = headSearch?.getAttribute('data-api')?.split('/api')[0] || '';
            const urlALM = `${baseApiUrl}/enrollments/${training.enrollment.id}/l1Feedback`;
            const url = `${getServiceURL}/api/v2/private/learning/feedback`;
            // Prepare questions array for submission
            const likeabilityMap = [
                "Strongly disagree",
                "Disagree",
                "OK",
                "Agree",
                "Strongly agree"
            ];
            const questions = surveyData?.data.attributes.questions.map(q => {
                let answer = '';
                if (q.questionType === 'likeability') {
                    // answers[q.questionId] is 1-5, map to likeabilityMap
                    const idx = Number(answers[q.questionId]) - 1;
                    answer = likeabilityMap[idx] || '';
                } else if (q.questionType === 'scaleTen') {
                    answer = answers[q.questionId] !== undefined && answers[q.questionId] !== null ? String(answers[q.questionId]) : '';
                } else if (q.questionType === 'text' || q.questionType === 'l1Qualitative') {
                    answer = answers[q.questionId] !== undefined && answers[q.questionId] !== null ? String(answers[q.questionId]) : '';
                }
                return {
                    answer,
                    questionId: q.questionId,
                    mandatory: q.mandatory,
                    questionType: q.questionType
                };
            }) || [];
            // Calculate score: average of all numeric answers (scaleTen only)
            const numericAnswers = questions
                .filter(q => (q.questionType === 'scaleTen') && q.answer !== undefined && q.answer !== null && q.answer !== '')
                .map(q => Number(q.answer));
            const score = numericAnswers.length > 0 ? Math.round(numericAnswers.reduce((a, b) => a + b, 0) / numericAnswers.length * 10) : 0;
            let body = JSON.stringify({
                data: {
                    id: training.enrollment.id,
                    type: 'feedback',
                    attributes: {
                        questions,
                        score: Number.isFinite(score) ? score : 0
                    }
                }
            });

            const respALM = await fetch(urlALM, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/vnd.api+json;charset=UTF-8',
                    'Accept': 'application/vnd.api+json;charset=UTF-8',
                    'authorization': `oauth ${getAccessToken()}`,
                },
                body
            });

            const getCountryCode = () => document.getElementsByName('x-country-code');
            const getPreferredLang = () => document.getElementsByName('x-preferred-language');
            const jwtValues = document.cookie.match(
                `(^|[^;]+)\\s*jwtToken\\s*=\\s*([^;]+)`
            );

            if (!respALM.ok) {
                const errorData = await respALM.json();
                if (errorData?.source?.info === 'Feedback for the LO is already given.' && matchedNotification) {
                    // PATCH notification to set actionTaken = true
                    const patchUrl = `${baseApiUrl}/users/${userId}/userNotifications/${matchedNotification.id}`;
                    const patchBody = JSON.stringify({
                        data: {
                            id: matchedNotification.id,
                            type: 'userNotification',
                            attributes: {
                                ...matchedNotification.attributes,
                                actionTaken: true
                            }
                        }
                    });
                    await fetch(patchUrl, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/vnd.api+json;charset=UTF-8',
                            'Accept': 'application/vnd.api+json;charset=UTF-8',
                            'authorization': `oauth ${getAccessToken()}`
                        },
                        body: patchBody
                    });
                    setShow(false);
                    setSubmitError('Feedback already given. Notification updated.');
                    setSubmitting(false);
                    return;
                } else {
                    throw new Error(errorData?.title || 'Failed to submit feedback');
                }
            }
            // PATCH notification to set actionTaken = true after successful feedback submission
            if (matchedNotification) {
                const patchUrl = `${baseApiUrl}/users/${userId}/userNotifications/${matchedNotification.id}`;
                const patchBody = JSON.stringify({
                    data: {
                        id: matchedNotification.id,
                        type: 'userNotification',
                        attributes: {
                            ...matchedNotification.attributes,
                            actionTaken: true
                        }
                    }
                });
                try {
                    await fetch(patchUrl, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/vnd.api+json;charset=UTF-8',
                            'Accept': 'application/vnd.api+json;charset=UTF-8',
                            'authorization': `oauth ${getAccessToken()}`
                        },
                        body: patchBody
                    });
                } catch (patchErr) {
                    // Optionally handle PATCH error, but do not block feedback success
                }
            }

            if (respALM.ok) {
                body = JSON.stringify({
                    data: {
                        id: `${training.enrollment.id}`,
                        type: 'feedback',
                        attributes: {
                            courseId: training.id,
                            questions: questions.map((q: any, index: number) => ({
                                ...q,
                                questionId: `${training.enrollment.id}_${q.questionId}`,
                                question: getLocalizedQuestion(surveyData?.data?.attributes?.questions[index] || {} as APIQuestion) || ''
                            })),
                            score: Number.isFinite(score) ? score : 0
                        }
                    }
                });

                await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-country-code': ((getCountryCode()[0] as HTMLInputElement)?.value || 'UK').toString(),
                        'x-application-id': 'anhcpproconnect',
                        'x-preferred-language': ((getPreferredLang()[0] as HTMLInputElement)?.value || 'en').toString(),
                        'x-secret-header': ((document.querySelector("input[name=x-secret-header]") as HTMLInputElement)?.value ?? '').toString(),
                        'x-id-token': (jwtValues?.pop() ?? "").toString()
                    },
                    body
                });
            }

            setTimeout(function () {
                const secondaryPopup = document.querySelector("#secondary-email-popup-trigger-modal") as HTMLElement || null;
                const enteringPopup = document.querySelector("#siteEnteringPopupFragmentPath") as HTMLElement || null;

                if (secondaryPopup) {
                    if (secondaryPopup.classList.contains('show')) {
                        secondaryPopup.classList.remove('d-none');
                    }
                }

                if (enteringPopup) {
                    if (enteringPopup.classList.contains('show')) {
                        enteringPopup.classList.remove('d-none');
                    }
                }
            }, 1000);

            setSubmitSuccess(true);
            setShow(false);
        } catch (err: any) {
            setSubmitError(err.message || 'Unknown error');
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;
    if (loading) return <div></div>; // hide message
    if (error) return <div>Error loading survey: {error}</div>;
    if (submitting) return <div>Submitting feedback...</div>;
    if (submitError) return <div>Error submitting feedback: {submitError}</div>;
    if (submitSuccess) return <div>Thank you for your feedback!</div>;

    const survey = surveyData || props.survey;

    return createPortal(
        <div className={styles.overlayContainer}>
            <form className={styles.popupContainer} onSubmit={handleSubmit}>
                <div className={styles.header}>
                    <div>
                        <div className={styles.title}>{courseBtnText.surveyTitle}</div>
                        <div className={styles.courseName}>Course: <em>{title}</em></div>
                    </div>
                    <button type="button" className={styles.closeIcon} onClick={handleClose} aria-label="Close">&times;</button>
                </div>
                <hr className={styles.divider} />
                <div className={styles.questionsScroll}>
                    {surveyData?.data.attributes.questions.map((q, idx) => (
                        <div className={styles.question} key={q.questionId}>
                            <div className={styles.questionText}>{idx + 1}. {getLocalizedQuestion(q)}</div>
                            {q.questionType === "scaleTen" && (
                                <>
                                    <div className={styles.scaleLabels}>
                                        <span>Not at all likely</span>
                                        <span>Extremely likely</span>
                                    </div>
                                    <div className={styles.radioGroup}>
                                        {[...Array(10)].map((_, i) => (
                                            <label className={styles.radioLabel} key={i}>
                                                <input
                                                    type="radio"
                                                    name={q.questionId}
                                                    value={i + 1}
                                                    checked={answers[q.questionId] === i + 1}
                                                    onChange={() => handleChange(q.questionId, i + 1)}
                                                />
                                                {i + 1}
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                            {q.questionType === "likeability" && (
                                <div className={styles.radioGroup}>
                                    {[
                                        "Strongly disagree",
                                        "Disagree",
                                        "OK",
                                        "Agree",
                                        "Strongly agree"
                                    ].map((label, i) => (
                                        <label className={styles.radioLabel} key={i}>
                                            <input
                                                type="radio"
                                                name={q.questionId}
                                                value={i + 1}
                                                checked={answers[q.questionId] === i + 1}
                                                onChange={() => handleChange(q.questionId, i + 1)}
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            )}
                            {(q.questionType === "text" || q.questionType.toLocaleLowerCase().includes('qualitative')) && (
                                <textarea
                                    className={styles.textarea}
                                    rows={3}
                                    value={answers[q.questionId] ?? ""}
                                    onChange={e => handleChange(q.questionId, e.target.value)}
                                    placeholder=""
                                />
                            )}
                        {errors[q.questionId] && (
                            <div className={styles.requiredText}>{courseBtnText.requiredMessage}</div>
                            )}
                        </div>
                    ))}
                </div>
                <div className={styles.buttonRow}>
                    <button type="submit" className={styles.doneButton}>{courseBtnText.doneButtonText}</button>
                </div>
                {courseBtnText.mlrApprovalCode && (<div className={styles.mlrContainer}><strong>{courseBtnText.mlrApprovalCode}</strong></div>)}
            </form>
        </div>, document.body
    );
};

export default Survey;
